// ─────────────────────────────────────────────────────────────────────────────
// PinCalibrationGuide.js — Step-by-step NK pin calibration walkthrough.
//
// Mounted inside OarlockDeviceCard for each side when `pin_calibration` is
// checked. The 6 steps follow NK's SpeedCoach calibration flow. State is
// stored per-pin (one OarlockConfig row per side, already keyed by
// vendor + boat + seat + side) so a rower can complete steps over multiple
// sessions and pick up where they left off.
//
// Auto-saves on every step toggle (rower is often in the boat) — uses the
// caller-provided onChange callback which patches the side form + triggers
// the row's existing save flow.
// ─────────────────────────────────────────────────────────────────────────────

import { useMemo } from 'react';
import { CheckCircle2, Circle, ListChecks, RotateCcw } from 'lucide-react';

export const PIN_CAL_STEPS = [
  {
    key: 'mount_tool',
    label: 'Mount Tool',
    detail: 'Fit the calibration tool onto the magnet bar (stern side) with A&B facing up, pressing it flush.',
  },
  {
    key: 'calibrate_ab',
    label: 'Calibrate A / B',
    detail: 'Follow the SpeedCoach prompts: move the oarlock to peg A (press Enter), then peg B (press Enter).',
  },
  {
    key: 'calibrate_cd',
    label: 'Calibrate C / D',
    detail: 'Remove the tool, flip it to the C&D side, and repeat the prompt sequence.',
  },
  {
    key: 'rotation_swing',
    label: 'Rotation / Swing Test',
    detail: 'Remove the tool. Smooth continuous rotation past 90° each side. Complete 8 "good" swings in 1 minute (~2 s per swing).',
  },
  {
    key: 'set_zero_angle',
    label: 'Set Zero Angle',
    detail: 'In the menu, select Set Zero Angle, align the oarlock perpendicular to the boat, press Enter.',
  },
  {
    key: 'set_zero_force',
    label: 'Set Zero Force',
    detail: 'Select Set Zero Force with NO oar in the lock to clear residual strain-gauge readings.',
  },
];

function fmtDate(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return null; }
}

export default function PinCalibrationGuide({
  vendor,
  side,
  completedSteps = [],
  completedAt = null,
  onChange,                  // (nextSteps: string[], nextCompletedAt: string|null) => void
}) {
  const stepSet = useMemo(() => new Set(Array.isArray(completedSteps) ? completedSteps : []), [completedSteps]);
  const totalDone = stepSet.size;
  const isComplete = totalDone === PIN_CAL_STEPS.length;

  const toggle = (key) => {
    const next = new Set(stepSet);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    const arr = PIN_CAL_STEPS.map(s => s.key).filter(k => next.has(k));
    const doneAt = arr.length === PIN_CAL_STEPS.length ? new Date().toISOString() : null;
    onChange?.(arr, doneAt);
  };

  const reset = () => onChange?.([], null);

  return (
    <div
      className="mt-2 rounded-lg border border-zinc-200 dark:border-zinc-700 bg-zinc-50/60 dark:bg-zinc-900/40 overflow-hidden"
      data-testid={`${vendor}-${side}-pcal-guide`}
    >
      <div className="px-3 py-2 flex items-center justify-between border-b border-zinc-200 dark:border-zinc-700 bg-zinc-100/70 dark:bg-zinc-800/50">
        <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-zinc-700 dark:text-zinc-200">
          <ListChecks className="w-3.5 h-3.5" />
          Pin Calibration · {totalDone}/{PIN_CAL_STEPS.length}
          {isComplete && (
            <span className="ml-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300">
              Done
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {completedAt && (
            <span className="text-[10px] font-mono text-zinc-500 dark:text-zinc-400">
              {fmtDate(completedAt)}
            </span>
          )}
          {totalDone > 0 && (
            <button
              type="button"
              onClick={reset}
              data-testid={`${vendor}-${side}-pcal-reset`}
              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider text-zinc-500 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-white transition"
            >
              <RotateCcw className="w-3 h-3" />
              Reset
            </button>
          )}
        </div>
      </div>

      <ol className="divide-y divide-zinc-100 dark:divide-zinc-700" data-testid={`${vendor}-${side}-pcal-steps`}>
        {PIN_CAL_STEPS.map((step, i) => {
          const done = stepSet.has(step.key);
          return (
            <li
              key={step.key}
              className="px-3 py-2 flex items-start gap-2.5"
              data-testid={`${vendor}-${side}-pcal-step-${step.key}`}
            >
              <button
                type="button"
                onClick={() => toggle(step.key)}
                aria-checked={done}
                role="checkbox"
                className="mt-0.5 shrink-0"
                data-testid={`${vendor}-${side}-pcal-toggle-${step.key}`}
              >
                {done ? (
                  <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                ) : (
                  <Circle className="w-4 h-4 text-zinc-300 dark:text-zinc-600" />
                )}
              </button>
              <div className="flex-1 min-w-0">
                <p className={`text-xs font-bold ${done ? 'text-emerald-700 dark:text-emerald-300 line-through' : 'text-zinc-900 dark:text-white'}`}>
                  {i + 1}. {step.label}
                </p>
                <p className={`text-[11px] mt-0.5 ${done ? 'text-zinc-400 dark:text-zinc-500' : 'text-zinc-600 dark:text-zinc-300'}`}>
                  {step.detail}
                </p>
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
